import {
	createTheme,
	CssBaseline,
	ScopedCssBaseline,
	ScopedCssBaselineProps,
	ThemeOptions,
	ThemeProvider,
	TypographyVariant,
	useTheme,
} from "@mui/material";
import { ReactNode, useMemo } from "react";
import scrollbarThemeOverrides from "../core/scrollbar";
import {
	getScrollbarColourThemeOverrides,
	getScrollbarThicknessThemeOverrides,
	scrollbarStyleThemeOverrides,
} from "./scrollbar";
import { deepmerge } from "@mui/utils";

type CSSValue = string | number;
type CSSObject = {
	[key: string]: CSSValue | CSSObject;
};

export function themeToCssString(overrides: CSSObject, selector: string = "body", isRoot: boolean = true): string {
	let propertyRules = "";
	let nestedRules = "";

	Object.entries(overrides).forEach(([key, value]) => {
		if (typeof value !== "object") {
			// 1. Handle CSS Variables (top-level properties like --scrollbar-background or borderRadius: 10px)
			const prop = key.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
			const val = typeof value === "number" ? `${value}px` : value;
			propertyRules += `  ${key.startsWith("--") ? key : prop}: ${val};\n`;
		} else {
			// 2. Resolve Selector
			let resolvedSelector: string;

			if (key.includes("&")) {
				// If we are at the root 'body', replace '&' with '' to make it global
				// If we are deeper, replace '&' with the parent selector
				const replacement = isRoot && selector === "body" ? "" : selector;
				resolvedSelector = key.replace("&", replacement).trim();
			} else if (key.startsWith(":") || key.startsWith(".") || key.startsWith("[")) {
				resolvedSelector = `${selector}${key}`;
			} else {
				resolvedSelector = `${selector} ${key}`;
			}

			// 3. Recurse (passing isRoot: false so deeper levels don't strip selectors)
			nestedRules += themeToCssString(value as CSSObject, resolvedSelector, false);
		}
	});

	let cssString = "";
	if (propertyRules) {
		cssString = `${selector} {\n${propertyRules}}\n`;
	}

	return cssString + nestedRules;
}

export const ALL_TYPOGRAPHY_VARIANTS = [
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"body1",
	"body2",
	"subtitle1",
	"subtitle2",
	"button",
	"caption",
	"overline",
] as const satisfies Readonly<TypographyVariant[]>;

export const FONT_FAMILY = '"Inter", "Roboto", sans-serif';

export const tooltipTheme: ThemeOptions["components"] = {
	MuiTooltip: {
		defaultProps: {
			arrow: true,
		},
		styleOverrides: {
			popper: {
				maxWidth: 525,
				maxHeight: 65,
			},
			tooltip: {
				fontSize: 14,
				fontWeight: 500,
				background: "#4C4C4C",
				"*": deepmerge(
					scrollbarStyleThemeOverrides,
					deepmerge(getScrollbarThicknessThemeOverrides(7), getScrollbarColourThemeOverrides("dark")),
				),
			},
			arrow: {
				color: "#4C4C4C",
			},
		},
	},
};

export const scrollbarTheme: ThemeOptions["components"] = {
	MuiPaper: {
		styleOverrides: {
			root: {
				...deepmerge(
					scrollbarStyleThemeOverrides,
					deepmerge(getScrollbarThicknessThemeOverrides(10), getScrollbarColourThemeOverrides("light")),
				),
			},
		},
	},
};

export const themeOptions: ThemeOptions = {
	palette: {
		primary: {
			main: "#0277ad",
			dark: "#42296E",
			light: "#ECE3FA",
		},
		action: {
			focus: "#0b6f85",
		},
	},
	spacing: (factor: number) => `${factor * 10}px`,
	typography: {
		fontFamily: FONT_FAMILY,
		button: {
			fontSize: 16,
		},
		allVariants: {
			fontFamily: FONT_FAMILY,
		},
		...Object.fromEntries(
			ALL_TYPOGRAPHY_VARIANTS.map((variant) => [variant, { fontFamily: FONT_FAMILY, lineHeight: 1.5 }]),
		),
	},
	breakpoints: {
		values: {
			xs: 0,
			sm: 768,
			md: 900,
			lg: 1200,
			xl: 1536,
		},
	},
	components: {
		MuiTypography: {
			styleOverrides: {
				root: {
					fontFamily: FONT_FAMILY, // Some ListItemText don't get overrided
				},
			},
		},
		MuiButton: {
			defaultProps: {
				disableFocusRipple: true,
				disableTouchRipple: true,
			},
			styleOverrides: {
				root: ({ theme }) => ({
					fontFamily: FONT_FAMILY,
					textTransform: "none",
					"& .MuiButton-startIcon": {
						marginLeft: 0,
					},
					"& .MuiButton-endIcon": {
						marginRight: 0,
					},
					"&:focus-visible": {
						outline: `2px solid ${theme.palette.action.focus}`,
						outlineOffset: "2px",
					},
					"&:hover": {
						backgroundColor: "#FAFAFA",
					},
				}),
			},
		},
		MuiToggleButtonGroup: {
			styleOverrides: {
				root: {
					backgroundColor: "#F8FAFB",
					border: "1px solid #22272C0A",
					borderRadius: "8px",
					gap: "5px",
					padding: "2px",
				},
			},
		},
		MuiToggleButton: {
			defaultProps: {
				disableFocusRipple: true,
				disableRipple: true,
			},
			styleOverrides: {
				root: {
					padding: "10px 16px",
					textAlign: "center",
					color: "#2C2F32",
					fontWeight: 400,

					"&.Mui-selected": {
						backgroundColor: "#42296E",
						border: "1px solid #DAD0EA",
						boxShadow: "0px 0px 4px 0px #00000008",
						borderRadius: "6px",
						fontWeight: 700,
						color: "#fff",

						// Crucial: Handle the hover state for the selected button!
						// MUI default styling makes the hover win over the selected background.
						"&:hover": {
							// Use the same background color as selected to maintain the state on hover
							backgroundColor: "#42296E",
						},
					},
					"&:focus-visible": {
						outline: "2px solid var(--focus-outline-color-dark)",
						outlineOffset: "3px",
					},
				},
			},
		},
		MuiSvgIcon: {
			styleOverrides: {
				root: ({ ownerState }) => ({
					...(ownerState.fontSize === "large" && {
						fontSize: "1.5rem",
					}),
					overflow: "visible",
				}),
			},
		},
		MuiTooltip: {
			defaultProps: {
				arrow: true,
			},
			styleOverrides: {
				tooltip: {
					fontSize: "13px",
					background: "#4C4C4C",
					color: "fff",
					borderRadius: "4px",
					padding: "10px",
					lineHeight: "1.6",
				},
				arrow: {
					color: "#4C4C4C",
				},
			},
		},
		MuiTextField: {
			defaultProps: {
				size: "small",
			},
		},
		MuiRadio: {
			styleOverrides: {
				root: {
					padding: "7px",
				},
			},
		},
		MuiFormControlLabel: {
			styleOverrides: {
				label: {
					fontSize: 18,
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				input: {
					padding: "10px",
					overflowX: "hidden",
					overflowY: "auto",
					textOverflow: "ellipsis",
					borderRadius: "4px",
					fontSize: 15,
					"&::placeholder": {
						color: "#6f6f6f",
						opacity: 1,
					},
					"&&": {
						boxSizing: "content-box",
					},
				},
				root: {
					backgroundColor: "#fff",
					borderRadius: "4px",
					color: "#4d4d4d",
					fontWeight: "400",
					margin: "2px",
					border: "1px solid #6f6f6f", // Borders for CoreTextBox, components that just use InputBase

					"&.Mui-focused:not(.Mui-error)": {
						border: "1px solid var(--focus-outline-color-dark)",
						boxShadow: "0px 0px 4px rgba(11, 111, 133, 0.25)",
					},
					"&.Mui-error:not(.Mui.disabled)": {
						border: "1px solid #bf0033",
					},
					"&.Mui-disabled": {
						color: "#948da2",
						background: "#f5f5f5",
						border: "1px #c4c4c4 solid",
						opacity: 1,
					},
					"&:hover:not(.Mui-disabled)": {
						backgroundColor: "#f5f5f5",
						boxShadow: "0 0 4px 0 #00000040",
					},
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					// Must explicitly tell Mui to not add their own border
					// (since it will cause double border since MuiInputBase and Fieldset is where border styles are defined)
					// Have to define border none for each state, since Mui injects border even though border none is set on root
					border: "none",
					// Fieldset is where border is defined for mui outlined input (CoreSelect, CoreDatePicker, CoreSearchField, etc)
					"& .MuiOutlinedInput-notchedOutline": {
						border: "1px solid #6f6f6f",
					},
					"&.Mui-focused:not(.Mui-error)": {
						border: "none",
						".MuiOutlinedInput-notchedOutline": {
							border: "2px solid var(--focus-outline-color-dark)",
						},
					},
					"&.Mui-error:not(.Mui.disabled)": {
						border: "none",
						".MuiOutlinedInput-notchedOutline": {
							border: "1px solid #bf0033",
						},
					},
					"&.Mui-disabled": {
						border: "none",
						".MuiOutlinedInput-notchedOutline": {
							border: "1px #c4c4c4 solid",
						},
					},
				},
				notchedOutline: {
					legend: {
						maxWidth: "0.01px",
					},
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				shrink: {
					"& + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend": {
						maxWidth: "100%",
					},
				},
			},
		},
		MuiSelect: {
			defaultProps: {
				displayEmpty: true,
				MenuProps: {
					PaperProps: {
						sx: {
							mt: "5px",
							padding: "2px",
							boxShadow: "0px 0px 2px 0px #00000040",
						},
					},
				},
			},
			styleOverrides: {
				select: {
					padding: "9px 15px",
					"&&": {
						paddingRight: "34px",
					},
				},
				icon: {
					right: "10px",
				},
				root: {
					height: "35px",
					minWidth: "fit-content",
					color: "#4D4D4D",
					".MuiPaper-root": {
						marginTop: "5px",
					},
					"&.Mui-focused:not(.Mui-error)": {
						borderWidth: "2px", // UX team wants select to have focus outline 2px thick
					},
				},
			},
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					padding: "9px 15px",
					fontSize: "13px",
					fontWeight: 400,
					color: "black",
					lineHeight: 1,
					minHeight: "unset",
					borderRadius: "4px",

					"& .MuiListItemIcon-root": {
						minWidth: "unset",
					},

					// Focus (keyboard or programmatic)
					"&.Mui-focused, &.Mui-focusVisible": {
						backgroundColor: "#fff",
						boxShadow: "inset 0 0 0 2px var(--focus-outline-color-dark)",
					},

					// Selected
					"&.Mui-selected": {
						backgroundColor: "#e7f1f3",

						// When selected + focused/visible, keep the same bg
						"&.Mui-focused, &.Mui-focusVisible": {
							// Keep selected background.
							// Need to explicity define, otherwise MUI injects its own styles
							backgroundColor: "#e7f1f3",
						},
					},

					"&:hover, &.Mui-selected:hover": {
						backgroundColor: "#ececec",
					},
				},
			},
		},
		MuiLink: {
			styleOverrides: {
				root: {
					color: "#0B6F85",
					textDecorationColor: "inherit",
					"&:focus-visible": {
						outline: "2px solid var(--focus-outline-color-dark)",
						borderRadius: "4px",
						outlineOffset: "2px",
					},
					"&:focus:not(:focus-visible)": {
						outline: "none", // Without this, if you click on a link, there is a focus outline because it's "focused"
					},
				},
			},
		},
		MuiSlider: {
			styleOverrides: {
				root: {
					borderRadius: "5px",
					height: "10px",
					width: "100%",
				},
				track: {
					backgroundColor: "#42296e",
					border: "none",
				},
				rail: {
					backgroundColor: "#E2E2E2",
					opacity: 1,
				},
				thumb: {
					backgroundColor: "#fff",
					border: "3px solid #42296E",
					boxShadow: "0px 1px 2px -1px #0000001A, 0px 1px 3px 0px #0000001A",

					"&.Mui-focusVisible": {
						outline: "2px solid var(--focus-outline-color-dark)",
						outlineOffset: "3px",
						boxShadow: "none",
					},

					"&:hover, &.Mui-active": {
						boxShadow: "0px 0px 0px 8px rgba(66, 41, 110, 0.16)",
					},
				},
			},
		},
		MuiPagination: {
			defaultProps: {
				showFirstButton: true,
				showLastButton: true,
			},
			styleOverrides: {
				root: {
					overflowX: "auto",
					"& .MuiPaginationItem-root": {
						backgroundColor: "#ffffff",
						border: "1px solid #f1f1f1",
						borderRadius: "25%",
						color: "#333333",
						fontWeight: 400,
						lineHeight: "normal",
						transition: "border 0.3s",
						margin: "4px",
						fontFamily: FONT_FAMILY,
						"&:hover": {
							backgroundColor: "#ebf6f9",
							borderColor: "#ebf6f9",
						},
						"&:active": {
							backgroundColor: "#b3d2d9",
							borderColor: "#b3d2d9",
						},
						"&:focus-visible": {
							backgroundColor: "#ebf6f9",
							boxShadow: `
            							0 0 0 2px #ebf6f9,
           	 							0 0 0 4px var(--focus-outline-color-dark)
          								`,
							borderRadius: "2px",
						},
						"&.Mui-disabled": {
							backgroundColor: "#ffffff",
							color: "#33333366",
							opacity: 1,
						},
						"& .MuiTouchRipple-root": {
							display: "none",
						},
					},

					// Selected state overrides
					"& .MuiPaginationItem-root.Mui-selected": {
						backgroundColor: "#0b6f85",
						color: "#ffffff",
						"&:hover": {
							backgroundColor: "#0b6f85",
						},
						"&:focus-visible": {
							backgroundColor: "#13859e",
						},
						"&:active": {
							backgroundColor: "#0b6f85",
						},
						"&.Mui-disabled": {
							backgroundColor: "#b0cbd1",
							color: "#305963",
						},
					},
				},
			},
		},
		MuiTableContainer: {
			styleOverrides: {
				root: {
					border: "1px solid #e0e0e0",
					borderColor: "#e0e0e0",
					borderStyle: "solid",
					borderRadius: "10px 10px 0px 0px",
					overflowX: "auto",
					stroke: "#e0e0e0",
					boxShadow: "none",
					borderCollapse: "separate",
					...scrollbarThemeOverrides,
					"*": scrollbarThemeOverrides,
				},
			},
		},
		MuiTable: {
			styleOverrides: {
				root: {
					fontSize: "15px",
				},
			},
		},
		MuiTableHead: {
			styleOverrides: {
				root: {
					fontSize: "15px",
					fontWeight: 400,
					backgroundColor: "#f7f6fb",
					textWrap: "nowrap",
				},
			},
		},
		MuiTableBody: {
			styleOverrides: {
				root: {
					fontWeight: 400,
					fontSize: "15px",
					color: "#575757",
					backgroundColor: "#ffffff",
					textOverflow: "ellipsis",
					textWrap: "nowrap",
					overflow: "hidden",
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					border: "1px solid #e0e0e0",
					backgroundColor: "inherit",
					textOverflow: "inherit",
					textWrap: "inherit",
					overflow: "inherit",
					"&.Mui-selected": {
						backgroundColor: "#E7F1F3",
					},
					"&.Mui-hover": {
						backgroundColor: "#ECECEC",
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					backgroundColor: "inherit",
					fontSize: "inherit",
					fontWeight: "inherit",
					color: "inherit",
					padding: "16px max(24px, min(40px, 2.6vw))",
					height: "60px",
					minHeight: "60px",
					boxSizing: "border-box",
					textOverflow: "inherit",
					textWrap: "inherit",
					overflow: "inherit",
					fontFamily: FONT_FAMILY,
					lineHeight: "1.5",
				},
			},
			variants: [
				{
					props: { variant: "inner" },
					style: { padding: "0px" },
				},
			],
		},
		MuiTableSortLabel: {
			styleOverrides: {
				root: {
					borderRadius: "4px",
					"&:focus-visible": {
						outline: "2px solid var(--focus-outline-color-dark)",
						outlineOffset: "7px",
					},
					"&:focus": {
						color: "inherit", // Overrides MUI making text gray on focus
					},
				},
				icon: {
					marginRight: "0px",
					marginLeft: "6px",
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					textTransform: "none",
					color: "#49454f",
					transition: "background-color 250ms",
					"&.Mui-selected": {
						color: "#1d1b20",
						fontWeight: "700",
					},
					"&:hover": {
						backgroundColor: "#1d1b2014",
					},
					"&:active": {
						backgroundColor: "#1d1b201f",
					},
					"&:focus-visible": {
						outline: "2px solid var(--focus-outline-color-dark)",
						// Ux teams wants border radius 5px and the outline to sit above the bottom border.
						// But negative outlineOffset causes borderRadius to look flatter,
						// so I eyeball the border radius to make it look like the figma file
						outlineOffset: "-7px",
						borderRadius: "10px",
					},
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				scrollButtons: {
					color: "#35363A",
					"&:hover": {
						backgroundColor: "#F2EFF7",
					},
					"&:active": {
						backgroundColor: "#E5DFF0",
					},
				},
			},
		},
		MuiSwitch: {
			defaultProps: {
				disableFocusRipple: true,
				disableTouchRipple: true,
			},
			styleOverrides: {
				root: {
					width: "50px",
					height: "27px",
					padding: 0,
					display: "flex",
					// switch base receives Mui-focusVisible but root gives the switch it's shape for outline
					":has(.Mui-focusVisible)": {
						outline: "2px solid var(--focus-outline-color-dark)",
						outlineOffset: "2px",
						borderRadius: "18px",
					},
					"&:active:not(:has(.Mui-disabled))": {
						"& .MuiSwitch-thumb": {
							width: 21,
						},
						"& .MuiSwitch-switchBase.Mui-checked": {
							transform: "translateX(23px)",
						},
					},
					"& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
						borderColor: "#FCFCFC",
						backgroundColor: "#FCFCFC",
					},
				},
				switchBase: {
					padding: "3px",
					"&.Mui-checked": {
						color: "#fff",
						transition: "transform 150ms ease-in-out",
						transform: "translateX(23px)",
						"& + .MuiSwitch-track": {
							opacity: 1,
							backgroundColor: "#0b6f85",
							borderColor: "#0b6f85",
						},
						"&.Mui-disabled": {
							"& + .MuiSwitch-track": {
								backgroundColor: "#b0cbd1",
								borderColor: "#B0CBD1",
								opacity: 1,
							},
						},
					},
					"&.Mui-disabled": {
						"& .MuiSwitch-thumb": {
							borderColor: "#cdcdcd",
						},
						"& + .MuiSwitch-track": {
							opacity: 1,
							backgroundColor: "#eeeeee",
							borderColor: "#cdcdcd",
						},
					},
				},
				thumb: {
					boxShadow: "0 0 3px rgba(0, 0, 0, 0.15)",
					boxSizing: "border-box",
					width: "21px",
					height: "21px",
					borderRadius: "9px",
					backgroundColor: "#FFFFFF",
					border: "1px solid #828282",
					transition: "transform 100ms ease-in-out, border-color 100ms, background-color 100ms",
				},
				track: {
					borderRadius: "18px",
					opacity: 1,
					backgroundColor: "#e4e4e4",
					boxSizing: "border-box",
					border: "1px solid #828282",
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					color: "#344054",
				},
				label: {
					fontFamily: FONT_FAMILY,
				},
			},
		},
		MuiScopedCssBaseline: {
			styleOverrides: {
				root: {
					...scrollbarThemeOverrides,
					"*": scrollbarThemeOverrides,
				},
			},
		},
		MuiCssBaseline: {
			styleOverrides: {
				...scrollbarThemeOverrides,
				"*": scrollbarThemeOverrides,
			},
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					"&:before": {
						display: "none",
					},
				},
			},
		},
		MuiAvatar: {
			styleOverrides: {
				root: {
					borderRadius: "50%",
					fontWeight: 400,
					fontSize: "0.75rem",
					lineHeight: "1.6",
				},
			},
		},
	},
};

export const dialogViewTheme: ThemeOptions = {
	components: {
		...tooltipTheme,
		MuiLink: themeOptions.components?.MuiLink,
	},
};

export default function ThemeWrapper({
	scoped = true,
	scopedCssBaselineProps,
	children,
}: {
	scoped?: boolean;
	scopedCssBaselineProps?: Omit<ScopedCssBaselineProps, "children">;
	children: ReactNode;
}) {
	const baseTheme = useTheme();
	const theme = useMemo(() => createTheme(baseTheme, themeOptions), [baseTheme]);

	return (
		<ThemeProvider theme={theme}>
			{scoped ? (
				<ScopedCssBaseline {...scopedCssBaselineProps}>{children}</ScopedCssBaseline>
			) : (
				<>
					<CssBaseline />
					{children}
				</>
			)}
		</ThemeProvider>
	);
}
